import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useMount } from 'react-use';

import { Boards, Tasks } from '@air/api';
import { type Task } from '@air/api/types';

import { type ImporterTaskEnhanced } from '~/components/Import/utils';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { removeTasksAction, updateTaskMetadataAction } from '~/store/tasks/actions';
import { IMPORTER_TASK_FALLBACK_BOARDS } from '~/swr-hooks/importer/utils';
import { getTasksFromLocalStorage } from '~/utils/taskUtils/storage';
import { useCreateTask } from '~/utils/taskUtils/useCreateTask';

const IMPORTS = 'IMPORTS';

export const getImportsKey = ({ workspaceId, accountId }: { workspaceId?: string; accountId?: string } = {}) => [
  IMPORTS,
  { workspaceId, ...(accountId ? { accountId } : {}) },
];

export const getImportsInProgress = (imports: ImporterTaskEnhanced[] = []) =>
  imports.filter(({ status }) => status === 'active' || status === 'pending');

export const getImportsStopped = (imports: ImporterTaskEnhanced[] = []) =>
  imports.filter(({ status, data }) => status === 'active' && data.storageExceeded);

export const useImportTasks = ({ refetchInterval }: { refetchInterval: number }) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: account } = useAccountContext();
  const { createTask } = useCreateTask();
  const dispatch = useDispatch();
  const workspaceId = currentWorkspace?.id;
  const accountId = account?.id;

  // At mount, cleanup local importer tasks that are not in the remote list
  useMount(() => {
    dispatch(
      removeTasksAction({
        taskIds: getTasksFromLocalStorage()
          .filter((task) => task.type === 'Importer' && !task.remoteTaskId)
          .map((task) => task.localTaskId),
      }),
    );
  });

  const { data: imports, isLoading } = useQuery({
    queryKey: getImportsKey({ workspaceId, accountId }),

    queryFn: async () => {
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      // // Load local importer tasks from localStorage
      const localTasks = getTasksFromLocalStorage().filter((task) => task.type === 'Importer');

      // Fetch remote importer tasks
      const { items } = await Tasks.listTasks({
        workspaceId,
        options: {
          limit: 100,
          type: 'Importer',
          accountId,
        },
      });

      const inProgressTasks = items.filter(({ status }) => status === 'active' || status === 'pending');

      const boardIds = inProgressTasks.map(({ args }) => args.parentBoardId).filter(Boolean) as string[];

      const boards = boardIds.length
        ? await Boards.listByIds({
            workspaceId,
            boardIds,
          })
        : [];

      const boardsMap = new Map(boards.map((board) => [board.id, board]));

      // Create local importer tasks for in-progress tasks that are not already in localStorage
      await Promise.all(
        inProgressTasks.map(async (task) => {
          const localTask = localTasks.find((t) => t.remoteTaskId === task.id);

          if (localTask) {
            dispatch(
              updateTaskMetadataAction({
                localTaskId: localTask.localTaskId,
                metadata: {
                  ...localTask.metadata,
                  ...task.args,
                  ...task.data,
                  board: {
                    ...localTask.metadata.board,
                    ...(task.args.parentBoardId
                      ? (boardsMap.get(task.args.parentBoardId) ?? IMPORTER_TASK_FALLBACK_BOARDS.WORKSPACE_ROOT)
                      : IMPORTER_TASK_FALLBACK_BOARDS.WORKSPACE_ROOT),
                  },
                  numRootFiles: task.args.files?.length ?? 0,
                  numRootFolders: task.args.folders?.length ?? 0,
                },
              }),
            );
            return Promise.resolve({
              data: {
                localTaskId: localTask.localTaskId,
                remoteTaskId: task.id,
              },
            });
          }

          return createTask({
            task: {
              metadata: {
                ...task.args,
                board: task.args.parentBoardId
                  ? boardsMap.get(task.args.parentBoardId)
                  : IMPORTER_TASK_FALLBACK_BOARDS.WORKSPACE_ROOT,
                numRootFiles: task.args.files?.length ?? 0,
                numRootFolders: task.args.folders?.length ?? 0,
              },
              type: 'Importer',
              createdAt: task.createdAt,
              updatedAt: task.updatedAt,
              workspaceId,
            },
            createTask: () => Promise.resolve(task as Task<'Importer'>),
            immediateStart: task.status === 'active',
          });
        }),
      );

      return {
        data: items.map((task) => ({
          ...task,
          metadata: {
            ...task.args,
            board: task.args.parentBoardId
              ? boardsMap.get(task.args.parentBoardId)
              : IMPORTER_TASK_FALLBACK_BOARDS.WORKSPACE_ROOT,
            numRootFiles: task.args.files?.length ?? 0,
            numRootFolders: task.args.folders?.length ?? 0,
          },
        })),
      };
    },
    refetchInterval: !refetchInterval ? false : refetchInterval,
    enabled: !!workspaceId && !!accountId,
  });

  const stoppedImports = useMemo(() => getImportsStopped(imports?.data), [imports?.data]);
  const inProgressImports = useMemo(() => getImportsInProgress(imports?.data), [imports?.data]);

  return {
    hasImportsInProgress: inProgressImports.length > 0,
    hasStoppedImports: stoppedImports.length > 0,
    stoppedImports,
    inProgressImports,
    imports: imports?.data ?? [],
    isLoading,
  };
};
